import React, { useState, useRef, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import "./CoursesSection.css";

const coursesData = [
  {
    id: 1,
    title: "Full Stack Web Development",
    description: "Master modern web development with React, Node.js, and MongoDB",
    category: "development",
    price: 0,
    duration: "12 weeks",
    level: "Beginner",
    rating: 4.8,
    students: 15420,
    tags: ["React", "Node.js", "MongoDB", "JavaScript"],
    image: "fas fa-code"
  },
  {
    id: 2,
    title: "Data Science Fundamentals",
    description: "Learn Python, Statistics, and Machine Learning from scratch",
    category: "data",
    price: 2999,
    duration: "16 weeks",
    level: "Intermediate",
    rating: 4.9,
    students: 8790,
    tags: ["Python", "Pandas", "Scikit-learn", "Statistics"],
    image: "fas fa-chart-line"
  },
  {
    id: 3,
    title: "UI/UX Design Mastery",
    description: "Create beautiful and functional user interfaces",
    category: "design",
    price: 1999,
    duration: "10 weeks",
    level: "Beginner",
    rating: 4.7,
    students: 12340,
    tags: ["Figma", "Adobe XD", "Prototyping", "User Research"],
    image: "fas fa-palette"
  },
  {
    id: 4,
    title: "Mobile App Development",
    description: "Build iOS and Android apps with React Native",
    category: "development",
    price: 3999,
    duration: "14 weeks",
    level: "Intermediate",
    rating: 4.6,
    students: 6780,
    tags: ["React Native", "iOS", "Android", "Firebase"],
    image: "fas fa-mobile-alt"
  },
  {
    id: 5,
    title: "Digital Marketing Strategy",
    description: "Master SEO, Social Media, and Content Marketing",
    category: "business",
    price: 1499,
    duration: "8 weeks",
    level: "Beginner",
    rating: 4.5,
    students: 9870,
    tags: ["SEO", "Social Media", "Content Marketing", "Analytics"],
    image: "fas fa-bullhorn"
  },
  {
    id: 6,
    title: "DevOps & Cloud Computing",
    description: "Learn AWS, Docker, Kubernetes, and CI/CD",
    category: "development",
    price: 4999,
    duration: "18 weeks",
    level: "Advanced",
    rating: 4.9,
    students: 4560,
    tags: ["AWS", "Docker", "Kubernetes", "Jenkins"],
    image: "fas fa-cloud"
  }
];

const filters = [
  { key: "all", label: "All Courses" },
  { key: "development", label: "Development" },
  { key: "design", label: "Design" },
  { key: "data", label: "Data Science" },
  { key: "business", label: "Business" }
];

const sortOptions = [
  { value: "popular", label: "Sort by Popular" },
  { value: "newest", label: "Sort by Newest" },
  { value: "price", label: "Sort by Price" },
  { value: "rating", label: "Sort by Rating" }
];

// Custom Dropdown Component
function CustomDropdown({ options, value, onChange, theme }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div 
      ref={dropdownRef} 
      className={`custom-dropdown theme-${theme} ${isOpen ? "open" : ""}`}
    >
      <button
        type="button"
        className="dropdown-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="dropdown-label">{selectedOption?.label || "Select"}</span>
        <span className={`dropdown-arrow ${isOpen ? "up" : ""}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path 
              d="M4 6L8 10L12 6" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {isOpen && (
        <ul className="dropdown-list" role="listbox">
          {options.map((option) => (
            <li
              key={option.value}
              className={`dropdown-item ${value === option.value ? "selected" : ""}`}
              onClick={() => handleSelect(option.value)}
              role="option"
              aria-selected={value === option.value}
            >
              {option.label}
              {value === option.value && (
                <span className="check-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M3 8L6.5 11.5L13 5" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CoursesSection({ theme = "light" }) {
  const [currentFilter, setCurrentFilter] = useState("all");
  const [sortBy, setSortBy] = useState("popular");
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });

  const filteredCourses = currentFilter === "all" 
    ? coursesData 
    : coursesData.filter(course => course.category === currentFilter);

  const enrollCourse = (courseId) => {
    const course = coursesData.find(c => c.id === courseId);
    if (course.price === 0) {
      alert(`Successfully enrolled in ${course.title}!`);
    } else {
      alert(`Enrolling in ${course.title} for ₹${course.price}`);
    }
  };

  return (
    <section ref={ref} className={`courses-section theme-${theme} ${inView ? "in-view" : ""}`}>
      <div className="section-header">
        <h2 className="section-title">Professional Courses</h2>
        <p className="section-subtitle">
          Master in-demand skills with our comprehensive courses designed by industry experts
        </p>
      </div>

      <div className="courses-header">
        <div className="course-filters">
          {filters.map((filter) => (
            <button
              key={filter.key}
              className={`filter-btn ${currentFilter === filter.key ? "active" : ""}`}
              onClick={() => setCurrentFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="sort-container">
          <CustomDropdown 
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
            theme={theme}
          />
        </div>
      </div>

      <div className="card-grid">
        {filteredCourses.map((course, index) => (
          <div key={course.id} className={`course-card card-${index + 1}`}>
            <div className="course-image">
              <i className={course.image}></i>
              <div className="course-duration">{course.duration}</div>
            </div>
            <div className="course-content">
              <div className="course-meta">
                <div className="course-level">{course.level}</div>
                <div className="course-rating">
                  <i className="fas fa-star"></i>
                  <span>{course.rating}</span>
                  <span style={{ color: "var(--text-muted)" }}>
                    ({course.students.toLocaleString()})
                  </span>
                </div>
              </div>
              <h3 className="course-title">{course.title}</h3>
              <p className="course-description">{course.description}</p>
              <div className="course-tags">
                {course.tags.map((tag) => (
                  <span key={tag} className="course-tag">{tag}</span>
                ))}
              </div>
              <div className="course-footer">
                <div className={`course-price ${course.price === 0 ? "free" : ""}`}>
                  {course.price === 0 ? "Free" : `₹${course.price}`}
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => enrollCourse(course.id)}
                >
                  {course.price === 0 ? "Start Learning" : "Enroll Now"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
